import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import LessonJournal from '../components/LessonJournal';
import ParentQuestionnaireForm from '../components/ParentQuestionnaireForm';
import SimpleMarkdown from '../components/SimpleMarkdown';
import TasksByTagProgressPanel from '../components/TasksByTagProgressPanel';
import {
  EMPTY_PARENT_QUESTIONNAIRE,
  parentQuestionnaireFromApi,
} from '../questionnaire/parentOptions';
import { formatDateTime, formatMoney } from './billingShared';

const TABS = [
  { id: 'lessons', label: 'Занятия' },
  { id: 'payment', label: 'Оплата' },
  { id: 'topics', label: 'Темы' },
  { id: 'activity', label: 'Активность' },
  { id: 'questionnaire', label: 'Анкета' },
];

const SELF_REPORT_LABELS = {
  EASY: '++ — получилось легко',
  OK_NO_PEEK: '+ — самостоятельно',
  OK_WITH_PEEK: '+- — с подсказкой / после сверки',
  OK_WITH_QUESTION: '+? — получилось, но есть вопрос',
  FAILED: '− — не получилось',
  NO_IDEAS: '−− — нет идей',
  NOT_TRIED: '! — даже не пробовал',
};

const VERDICT_LABELS = {
  ACCEPTED: 'Принято',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

function groupAttemptsByDay(attemptsList) {
  const groups = new Map();
  for (const a of attemptsList || []) {
    const key = a?.created_at ? new Date(a.created_at).toLocaleDateString('ru-RU') : '—';
    const arr = groups.get(key) || [];
    arr.push(a);
    groups.set(key, arr);
  }
  return Array.from(groups.entries());
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatFieldErrors(data) {
  if (!data || typeof data !== 'object') return 'Не удалось сохранить анкету';
  if (typeof data.detail === 'string') return data.detail;
  return Object.entries(data)
    .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
    .join('; ');
}

function PaymentTab({ studentId }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/payments/me/');
      const all = [
        ...(res.data?.pending_reports || []),
        ...(res.data?.recent_lesson_reports || []),
      ];
      const sid = Number(studentId);
      const seen = new Set();
      const filtered = [];
      for (const r of all) {
        if (Number(r.student_id) !== sid) continue;
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        filtered.push(r);
      }
      filtered.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
      setReports(filtered);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить журнал оплаты.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmReport(id) {
    setConfirmingId(id);
    setError('');
    try {
      await api.post(`/payments/lesson-reports/${id}/confirm/`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось подтвердить занятие.');
    } finally {
      setConfirmingId(null);
    }
  }

  if (loading) return <p className="text-secondary">Загрузка журнала оплаты…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!reports.length) {
    return (
      <p className="text-secondary">
        Пока нет записей оплаты по этому ребёнку. Семейный баланс и пополнение — на странице{' '}
        <Link to="/billing">Баланс</Link>.
      </p>
    );
  }

  return (
    <div>
      <p className="text-secondary">
        Тема, сумма и статус списания. Общий счёт семьи — на странице{' '}
        <Link to="/billing">Баланс</Link>.
      </p>
      <ul className="billing-lesson-history-list">
        {reports.map((r) => (
          <li key={r.id} className="billing-lesson-history-card">
            <div className="billing-lesson-history-meta">
              <strong>{r.topic || 'Занятие'}</strong>
              {' · '}
              {formatDateTime(r.created_at)}
              {' · '}
              {r.duration_minutes} мин · {formatMoney(r.amount)}
              {' · '}
              <span className="text-secondary">{r.status_label}</span>
            </div>
            {r.description ? (
              <div className="billing-report-details">
                <strong>Что делали:</strong> {r.description}
              </div>
            ) : null}
            {r.homework ? (
              <div className="billing-report-details billing-homework">
                <strong>ДЗ:</strong>
                <SimpleMarkdown text={r.homework} />
              </div>
            ) : null}
            {r.status === 'awaiting_student' ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={confirmingId === r.id}
                onClick={() => confirmReport(r.id)}
              >
                {confirmingId === r.id ? 'Сохранение…' : 'Подтвердить'}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopicsTab({ studentId }) {
  const [mainTab, setMainTab] = useState('notes');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/teacher/students/${studentId}/topic-progress-notes/`);
        if (!cancelled) setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          const msg = e.response?.data?.detail || 'Не удалось загрузить заметки.';
          setError(typeof msg === 'string' ? msg : 'Не удалось загрузить заметки.');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  return (
    <div>
      <div className="topic-progress-main-tabs" role="tablist" aria-label="Вид прогресса">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'notes'}
          className={`btn btn-sm ${mainTab === 'notes' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setMainTab('notes')}
        >
          Заметки по темам
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'journal'}
          className={`btn btn-sm ${mainTab === 'journal' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setMainTab('journal')}
        >
          Журнал задач по темам
        </button>
      </div>

      {mainTab === 'journal' ? (
        <div className="topic-progress-stub-card" role="tabpanel">
          <TasksByTagProgressPanel studentId={studentId} />
        </div>
      ) : (
        <div className="topic-progress-notes-panel" role="tabpanel">
          {loading ? <p className="text-secondary">Загрузка…</p> : null}
          {error ? <div className="alert alert-error">{error}</div> : null}
          {!error && !loading && items.length === 0 ? (
            <p className="text-secondary">Нет активных связей с преподавателями — пока нечего показать.</p>
          ) : null}
          {items.map((item) => (
            <section key={item.teacher_id} className="progress-note-card progress-note-card-expanded">
              <h2 className="progress-note-title">{item.teacher_display_name}</h2>
              <div className="progress-note-card-inner">
                <div className="progress-note-meta text-secondary">
                  Обновлено:{' '}
                  {item.updated_at
                    ? new Date(item.updated_at).toLocaleString('ru-RU')
                    : 'ещё не обновлялось'}
                </div>
                <pre className="progress-note-readonly">{item.notes || '—'}</pre>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityTab({ studentId }) {
  const [days, setDays] = useState(30);
  const [problemOnly, setProblemOnly] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAttempts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        days: days === 0 ? undefined : days,
        problem: problemOnly ? 1 : undefined,
      };
      const res = await api.get(`/parent/children/${studentId}/attempts/`, { params });
      setAttempts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось загрузить активность.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить активность.');
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [studentId, days, problemOnly]);

  useEffect(() => {
    void loadAttempts();
  }, [loadAttempts]);

  return (
    <div>
      <p className="text-secondary">
        Что решали на сайте: самооценка ученика и комментарий преподавателя. Код не показываем.
      </p>
      <div className="parent-journal-filters">
        <label className="text-secondary">
          Период
          <select className="form-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={0}>всё время</option>
          </select>
        </label>
        <label className="parent-journal-check">
          <input
            type="checkbox"
            checked={problemOnly}
            onChange={(e) => setProblemOnly(e.target.checked)}
          />
          только проблемные
        </label>
      </div>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {loading ? (
        <p className="text-secondary">Загрузка…</p>
      ) : attempts.length === 0 ? (
        <p className="text-secondary">Пока нет событий.</p>
      ) : (
        <div className="parent-attempt-groups">
          {groupAttemptsByDay(attempts).map(([day, items]) => (
            <section key={day} className="parent-attempt-day">
              <h2>{day}</h2>
              <ul className="parent-attempt-list">
                {items.map((a) => (
                  <li key={a.id} className="parent-attempt-card">
                    <div className="parent-attempt-head">
                      <strong>{a.task_title || a.task_slug || 'Задача'}</strong>
                      <span className="text-secondary">{fmtDateTime(a.created_at)}</span>
                    </div>
                    {a.task_list_title ? (
                      <div className="text-secondary">Список: {a.task_list_title}</div>
                    ) : null}
                    <div className="parent-attempt-meta">
                      Ученик: {SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || 'без самооценки'}
                      {a.teacher_verdict ? (
                        <>
                          {' · '}
                          преподаватель: {VERDICT_LABELS[a.teacher_verdict] || a.teacher_verdict}
                        </>
                      ) : null}
                    </div>
                    {a.self_comment ? (
                      <div className="parent-attempt-comment">
                        <strong>Отчёт ученика</strong>
                        <pre className="progress-note-readonly">{a.self_comment}</pre>
                      </div>
                    ) : null}
                    {a.teacher_comment ? (
                      <div className="parent-attempt-comment">
                        <strong>Комментарий преподавателя</strong>
                        <pre className="progress-note-readonly">{a.teacher_comment}</pre>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionnaireTab({ studentId }) {
  const [form, setForm] = useState({ ...EMPTY_PARENT_QUESTIONNAIRE });
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previouslyFilled, setPreviouslyFilled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/parent/children/${studentId}/questionnaire/`);
        if (cancelled) return;
        setForm(parentQuestionnaireFromApi(res.data));
        setPreviouslyFilled(Boolean(res.data?.filled));
        setStudentName(res.data?.student?.display_name || res.data?.student?.username || '');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Не удалось загрузить анкету');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.put(`/parent/children/${studentId}/questionnaire/`, form);
      setForm(parentQuestionnaireFromApi(res.data));
      setPreviouslyFilled(true);
      setSuccess('Анкета сохранена');
    } catch (err) {
      setError(formatFieldErrors(err.response?.data));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-secondary">Загрузка анкеты…</p>;

  return (
    <ParentQuestionnaireForm
      form={form}
      onChange={setForm}
      onSubmit={handleSubmit}
      saving={saving}
      error={error}
      success={success}
      studentName={studentName}
      submitLabel={previouslyFilled ? 'Сохранить изменения' : 'Отправить анкету'}
    />
  );
}

export function ParentChildTabRedirect({ tab }) {
  const { studentId } = useParams();
  return <Navigate to={`/parent/children/${studentId}?tab=${tab}`} replace />;
}

export default function ParentChildPage() {
  const { studentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'lessons';
  const tab = TABS.some((t) => t.id === rawTab) ? rawTab : 'lessons';

  const [childName, setChildName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/parent/children/');
        const list = Array.isArray(res.data) ? res.data : [];
        const child = list.find((c) => Number(c.id) === Number(studentId));
        if (!cancelled) setChildName(child ? child.display_name : '');
      } catch {
        if (!cancelled) setChildName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  function setTab(next) {
    setSearchParams(next === 'lessons' ? {} : { tab: next }, { replace: true });
  }

  return (
    <div className="parent-home-page parent-child-page">
      <div className="page-header">
        <p className="page-subtitle">
          <Link to="/parent" className="back-link">← Кабинет родителя</Link>
        </p>
        <h1>{childName || 'Ребёнок'}</h1>
        <p className="page-subtitle">
          Журнал занятий, оплата, темы и активность на сайте.
          {' · '}
          <Link to={`/my-schedule?student_id=${studentId}`}>Расписание</Link>
          {' · '}
          <Link to={`/my-messages?student_id=${studentId}`}>Сообщения</Link>
        </p>
      </div>

      <div className="parent-child-tabs" role="tablist" aria-label="Разделы">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="parent-child-tab-panel" role="tabpanel">
        {tab === 'lessons' ? (
          <>
            <p className="text-secondary">
              Что было на уроках с преподавателем: дата, краткий отчёт и комментарии.
            </p>
            <LessonJournal mode="parent" studentId={studentId} />
          </>
        ) : null}
        {tab === 'payment' ? <PaymentTab studentId={studentId} /> : null}
        {tab === 'topics' ? <TopicsTab studentId={studentId} /> : null}
        {tab === 'activity' ? <ActivityTab studentId={studentId} /> : null}
        {tab === 'questionnaire' ? <QuestionnaireTab studentId={studentId} /> : null}
      </div>
    </div>
  );
}
